import React, { useEffect, useMemo, useRef } from 'react';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';

import { FOMO_NUDGE_ID } from '@atlassian/rovo-fomo-nudge/constants';
import { messages } from '@atlassian/rovo-fomo-nudge/i18n';
import { createFomoNudgeAction } from '@atlassian/rovo-fomo-nudge/create-fomo-nudge-action';
import { useFomoCollaboratorsBatched } from '@atlassian/rovo-fomo-nudge/use-fomo-collaborators-batched';
import { useCanShowFomoNudge } from '@atlassian/rovo-fomo-nudge/use-can-show-fomo-nudge';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

import { DeferIfNudgePresent } from './DeferIfNudgePresent.tsx';
import { parseTopCollaboratorAccountIds } from './normalizeTopCollaboratorAccountIds.tsx';
import {
	TOP_ROVO_CHAT_COLLABORATORS_TRAIT,
	useTopRovoChatCollaboratorsTraitResource,
} from './useTopRovoChatCollaboratorsTraitResource.tsx';

/** localStorage key for the FOMO nudge "shown once" guard. Registered in atlassian-cookies (ENDS_WITH pattern). */
const FOMO_NUDGE_DISMISSED_KEY = 'rovo.fomo.nudge.dismissed';

/** How long to suppress the FOMO nudge after the user clicks or dismisses it (14 days). */
const FOMO_NUDGE_SUPPRESSION_DURATION_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Jira localStorage-backed FomoNudgeStorage adapter.
 *
 * Uses AtlBrowserStorageLocal (from @atlassian/browser-storage-controls) so
 * the key is subject to the browser-storage consent registry. Errors are
 * reported via fireErrorAnalytics per Jira convention.
 */
const jiraFomoNudgeMarkShown = (): void => {
	// @browser-storage setter
	AtlBrowserStorageLocal.setItem(FOMO_NUDGE_DISMISSED_KEY, String(Date.now())).then((result) => {
		if (
			result &&
			(result.status === 'ERROR' || result.status === 'FAILED' || result.status === 'BLOCKED')
		) {
			fireErrorAnalytics({
				meta: {
					id: 'aiRovoFomoNudge.markShown',
					packageName: 'jiraAiRovoActionsRouteProvider',
					teamName: 'Crocs',
				},
				error:
					'error' in result ? result.error : new Error('Failed to persist FOMO nudge dismissal'),
				attributes: { operation: 'AtlBrowserStorageLocal.setItem', status: result.status },
			});
		}
	});
};

const jiraFomoNudgeHasBeenShown = (): boolean => {
	try {
		const dismissedAt = AtlBrowserStorageLocal.getItem(FOMO_NUDGE_DISMISSED_KEY);
		return !!dismissedAt && Date.now() - Number(dismissedAt) < FOMO_NUDGE_SUPPRESSION_DURATION_MS;
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'aiRovoFomoNudge.hasBeenShown',
				packageName: 'jiraAiRovoActionsRouteProvider',
				teamName: 'Crocs',
			},
			error:
				error instanceof Error ? error : new Error('Failed to read FOMO nudge dismissal state'),
			attributes: { operation: 'AtlBrowserStorageLocal.getItem' },
		});
		return false;
	}
};

/**
 * Phasing:
 *   1 — wired + experiment-gated; no UI shown (this skeleton).
 *   2 — avatar UI + i18n via shared package.
 *   3 — non-Rovo-MAU TAP trait gate.
 *   4 — collaborator data via TWG / AGG.
 *   5 — AtlBrowserStorageLocal "show once" adapter (30-day TTL). ✓ Done.
 *
 * Gating model: the killswitch gate is applied to the OUTER
 * `AiRovoFomoNudgeProviderDeferring` wrapper that the route map mounts. The
 * bare `AiRovoFomoNudgeProvider` is exported gate-free so unit tests can
 * exercise the experiment branch directly without standing up the deferring
 * machinery, and so that the deferring wrapper does the only `fg()` check.
 */
export const AiRovoFomoNudgeProvider = (): null => {
	const { formatMessage } = useIntl();
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasTrackedQualificationRef = useRef(false);

	// Cookie consent check — the hook reads the `rovo_fomo_nudge_cookie_policy_check`
	// killswitch internally. When the killswitch is on, the nudge only shows for
	// users who have consented to Functional storage. When the killswitch is off,
	// canShowFromCookiePreference is `true` immediately (legacy behavior, no network/storage check).
	// Runs before any TAP / Relay fetch so non-consenting users incur no extra
	// network requests.
	const canShowFromCookiePreference = useCanShowFomoNudge();

	// Read suppression once per render. The adapter's own try/catch +
	// fireErrorAnalytics already handles localStorage exceptions, so no extra
	// wrapper is needed here. Used to short-circuit the trait + Relay fetches
	// below so users inside the 14-day suppression window incur no network
	// requests.
	const suppressed = jiraFomoNudgeHasBeenShown();

	const {
		collaboratorAaids: topCollaboratorAAIDs,
		isRovoChatMau,
		loaded: traitsLoaded,
	} = useTopRovoChatCollaboratorsTraitResource(
		undefined,
		canShowFromCookiePreference === true && !suppressed,
	);
	const collaboratorAccountIds = useMemo(
		() => parseTopCollaboratorAccountIds(topCollaboratorAAIDs),
		[topCollaboratorAAIDs],
	);
	const {
		collaborators,
		isReady,
		error: collaboratorError,
	} = useFomoCollaboratorsBatched(collaboratorAccountIds, {
		enabled:
			canShowFromCookiePreference === true &&
			!suppressed &&
			traitsLoaded &&
			isRovoChatMau === false &&
			collaboratorAccountIds.length > 0 &&
			expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false),
	});

	useEffect(() => {
		// Bail unless: cookie consent granted, suppression window has passed, trait
		// resource has resolved, collaborator fetch ready, the user is not a Rovo
		// Chat MAU, and the user is in the experiment treatment group.
		//
		// The explicit `expVal` check here is a safety backstop: the collaborator
		// fetch's `enabled` flag already includes the same `expVal` guard, but we
		// do NOT rely on `useFomoCollaboratorsBatched` keeping `isReady === false`
		// when `enabled === false`. That contract is undocumented and out of this
		// package's control, so we re-check `expVal` here to guarantee that no
		// `traitResolved` / `collaboratorResolution failed` analytics — nor any
		// nudge mount — can ever escape to out-of-treatment users.
		if (
			canShowFromCookiePreference !== true ||
			suppressed ||
			!traitsLoaded ||
			!isReady ||
			isRovoChatMau !== false ||
			!expVal('rovo_button_collab_teammates_fomo_nudge', 'isEnabled', false)
		)
			return;

		if (!hasTrackedQualificationRef.current) {
			hasTrackedQualificationRef.current = true;
			fireTrackAnalytics(createAnalyticsEvent({}), 'fomoNudge traitResolved', {
				traitName: TOP_ROVO_CHAT_COLLABORATORS_TRAIT,
				aaidCount: collaboratorAccountIds.length,
			});

			const zeroResolved = collaboratorAccountIds.length > 0 && collaborators.length === 0;
			if (collaboratorError || zeroResolved) {
				let errorMessage: string;
				if (collaboratorError instanceof Error) {
					errorMessage = collaboratorError.message;
				} else if (collaboratorError) {
					errorMessage = String(collaboratorError);
				} else {
					errorMessage = 'no collaborators resolved';
				}
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'fomoNudgeCollaboratorResolution failed',
					{
						operation: 'fetchUsersByAaids',
						requestedAaidCount: collaboratorAccountIds.length,
						errorMessage,
					},
				);
				if (collaboratorError) {
					fireErrorAnalytics({
						meta: {
							id: 'aiRovoFomoNudge.collaboratorResolution',
							packageName: 'jiraAiRovoActionsRouteProvider',
							teamName: 'crocs',
						},
						error:
							collaboratorError instanceof Error
								? collaboratorError
								: new Error(String(collaboratorError)),
						attributes: {
							operation: 'fetchUsersByAaids',
						},
					});
				}
			}
		}

		if (collaborators.length === 0) {
			return;
		}

		const action = createFomoNudgeAction({
			collaborators,
			title: formatMessage(messages.fomoNudgeTitle),
			description: formatMessage(messages.fomoNudgeDescription),
			onAccept: jiraFomoNudgeMarkShown,
			onDismiss: jiraFomoNudgeMarkShown,
		});

		// NOTE: route-scoped — this provider is mounted inside each Rovo-capable
		// route's `nudges: () => ...` slot in jiraRouteToConfigProvider, so unmount
		// on route change cleans up automatically. On routes that also mount another
		// nudge provider (e.g. AiRovoIssueViewNudgesProvider), the deferring
		// wrapper around this component yields when a non-self nudge is already in
		// the store. The route map MUST render this provider FIRST in the Fragment
		// so the sibling becomes the last writer; see DeferIfNudgePresent.
		updateConfiguration({ nudges: [action] });
	}, [
		canShowFromCookiePreference,
		suppressed,
		updateConfiguration,
		formatMessage,
		collaborators,
		isReady,
		createAnalyticsEvent,
		collaboratorAccountIds,
		topCollaboratorAAIDs,
		collaboratorError,
		traitsLoaded,
		isRovoChatMau,
	]);

	return null;
};

/** FOMO pre-wrapped to yield when another nudge is already in the store. */
const AiRovoFomoNudgeProviderDeferringInner: React.FC<{}> = () => (
	<DeferIfNudgePresent ownNudgeIds={[FOMO_NUDGE_ID]}>
		<AiRovoFomoNudgeProvider />
	</DeferIfNudgePresent>
);

export const AiRovoFomoNudgeProviderDeferring: React.FC<{}> = componentWithFG(
	// skillswitch gate controlled by is_rovo_chat_mau
	'rovo_button_collab_teammates_fomo_nudge_enabled',
	AiRovoFomoNudgeProviderDeferringInner,
	() => null,
);
